import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { STATUS_LABEL, STATUS_BADGE, LOAN_PRODUCTS } from "@/lib/loanProducts";
import { ArrowRight, FileText, Wallet, Clock, CheckCircle2 } from "lucide-react";

const fmt = (v) => `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

export default function ClientDashboard() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loans, setLoans] = useState([]);

  useEffect(() => {
    api.get("/applications/me").then(({ data }) => setApps(data || []));
    api.get("/loans/me").then(({ data }) => setLoans(data || []));
  }, []);

  const activeLoans = loans.filter((l) => l.status === "active");
  const totalOutstanding = activeLoans.reduce((s, l) => s + (l.balance || 0), 0);
  const pendingApps = apps.filter((a) => ["submitted", "under_review"].includes(a.status)).length;

  const productTitle = (key) => LOAN_PRODUCTS.find((p) => p.key === key)?.title || key;

  return (
    <DashboardLayout role="client">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-semibold text-ink sm:text-3xl">Welcome back, {user?.full_name?.split(" ")[0]}.</h1>
        <p className="mt-1 text-sm text-ink-soft">Here&apos;s a snapshot of your finances with Joseph Roberts Capital.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={FileText} label="Applications" value={apps.length} sub={`${pendingApps} pending`} />
        <StatCard icon={Wallet} label="Active loans" value={activeLoans.length} sub={`${loans.length} total`} />
        <StatCard icon={Clock} label="Outstanding" value={fmt(totalOutstanding)} sub="Across active loans" />
        <StatCard icon={CheckCircle2} label="Completed loans" value={loans.filter((l) => l.status === "completed").length} sub="Fully repaid" />
      </div>

      <div className="mt-6 rounded-xl border border-[#E5E2DE] bg-gradient-to-br from-[#F9F6F0] to-white p-5" data-testid="client-quickpay">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-xl">
            <h2 className="font-heading text-lg font-semibold text-ink">Quick pay</h2>
            <p className="mt-1 text-sm text-ink-soft">
              Make an ad-hoc payment via Paynow (EcoCash, OneMoney or Visa). Please add your name or loan reference on the Paynow page so we can match it to your account.
            </p>
            <p className="mt-2 text-xs text-ink-muted">
              Paying a specific installment? Open your loan and use{" "}
              <Link to="/dashboard/loans" className="font-semibold text-[#5B2C82] hover:text-[#E87722]">Pay now</Link>{" "}
              instead — it updates your schedule automatically.
            </p>
          </div>
          <a
            href="https://www.paynow.co.zw/Payment/Link/?q=c2VhcmNoPWpvZS50YW5kaSU0MGdtYWlsLmNvbSZhbW91bnQ9MS4wMCZyZWZlcmVuY2U9Jmw9MQ%3d%3d"
            target="_blank"
            rel="noreferrer"
            className="shrink-0 self-start transition-opacity hover:opacity-90"
            data-testid="client-quickpay-paynow-btn"
          >
            <img
              src="https://www.paynow.co.zw/Content/Buttons/Medium_buttons/button_pay-now_medium.png"
              alt="Pay now with Paynow"
              className="h-12 w-auto"
            />
          </a>
        </div>
      </div>


      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <Section title="Recent applications" link="/dashboard/applications" linkLabel="View all">
          {apps.length === 0 ? (
            <Empty>No applications yet. <Link to="/apply" className="font-semibold text-[#5B2C82] hover:text-[#E87722]">Apply now →</Link></Empty>
          ) : (
            <ul className="divide-y divide-[#E5E2DE]">
              {apps.slice(0, 4).map((a) => (
                <li key={a.id} className="flex items-center justify-between py-3" data-testid={`client-app-${a.id}`}>
                  <div className="min-w-0">
                    <div className="truncate font-heading text-sm font-semibold text-ink">{productTitle(a.product)}</div>
                    <div className="text-xs text-ink-muted">{fmt(a.amount)} · {a.term_months} mo · {new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs ${STATUS_BADGE[a.status]}`}>{STATUS_LABEL[a.status]}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Active loans" link="/dashboard/loans" linkLabel="Manage loans">
          {activeLoans.length === 0 ? (
            <Empty>No active loans yet.</Empty>
          ) : (
            <ul className="divide-y divide-[#E5E2DE]">
              {activeLoans.slice(0, 4).map((l) => (
                <li key={l.id} className="flex items-center justify-between py-3">
                  <div className="min-w-0">
                    <div className="truncate font-heading text-sm font-semibold text-ink">{productTitle(l.product)}</div>
                    <div className="text-xs text-ink-muted">Balance: {fmt(l.balance)} of {fmt(l.total_repayable)}</div>
                  </div>
                  <Link to={`/dashboard/loans/${l.id}`} className="text-xs font-semibold text-[#5B2C82] hover:text-[#E87722]" data-testid={`client-loan-link-${l.id}`}>
                    Details <ArrowRight className="inline h-3 w-3" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </DashboardLayout>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-[#E5E2DE] bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
        <Icon className="h-4 w-4 text-[#5B2C82]" />
      </div>
      <div className="mt-2 font-heading text-2xl font-bold text-ink">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-ink-muted">{sub}</div>}
    </div>
  );
}

function Section({ title, children, link, linkLabel }) {
  return (
    <div className="rounded-xl border border-[#E5E2DE] bg-white p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold text-ink">{title}</h2>
        {link && <Link to={link} className="text-xs font-semibold text-[#5B2C82] hover:text-[#E87722]">{linkLabel}</Link>}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Empty({ children }) {
  return <div className="rounded-lg bg-[#F9F6F0] px-4 py-6 text-center text-sm text-ink-soft">{children}</div>;
}
